<script setup lang="ts">
import '@/assets/scss/pages/_driver.scss';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import ButtonsCell from '@/components/drivers/ButtonsCell.vue';
import DriveStartChecklistPopup from '@/components/drivers/DriveStartChecklistPopup.vue';
import DriveStatusCell from '@/components/drivers/DriveStatusCell.vue';
import FlightCell from '@/components/drivers/FlightCell.vue';
import LocationCell from '@/components/drivers/LocationCell.vue';
import UnitsList from '@/components/drivers/UnitsList.vue';
import UnitEdit from '@/components/transports/popups/UnitEdit.vue';
import { useDrivesHub } from '@/composables/useDrivesHub';
import { useStartWarnings } from '@/composables/useStartWarnings';
import { RoleGroups } from '@/constants/Roles';
import { toStartTime } from '@/helpers/dateTimeFormatters';
import type { IDriveListResponse, IDriveStartRequest } from '@/types/interfaces/DriveTypes';
import Icons from '@/constants/Icons';
import { DxButton } from 'devextreme-vue/button';
import { DxDataGrid, DxKeyboardNavigation, DxMasterDetail, DxItem, DxToolbar, DxColumn, DxSelection, type DxDataGridTypes } from 'devextreme-vue/data-grid';
import { shallowRef, nextTick } from 'vue';

definePage({
  name: 'DriverRoot',
  meta: {
    title: 'drivers.title',
    driver: true,
    roles: RoleGroups.Driver
  }
});

const checklistPopupRef = shallowRef<typeof DriveStartChecklistPopup>();
const drivesGridRef = shallowRef<DxDataGrid>();
const unitListRef = shallowRef<typeof UnitsList>();
const unitAddRef = shallowRef<typeof UnitEdit>();
const { dataSource } = useDrivesHub(() => drivesGridRef.value?.instance);
const { rowPrepared } = useStartWarnings(() => drivesGridRef.value?.instance);

function expandRow(id: number): void {
  const grid = drivesGridRef.value?.instance;
  if (!grid) {
    return;
  }

  grid.collapseAll(-1);
  grid.expandRow(id);
  grid.navigateToRow(id);
}

function collapseRows(): void {
  drivesGridRef.value?.instance?.collapseAll(-1);
}

async function rowExpanded(e: DxDataGridTypes.RowExpandedEvent<IDriveListResponse, number>): Promise<void> {
  const rowIndex = e.component.getRowIndexByKey(e.key);
  e.component.repaintRows([ rowIndex ]);
  await nextTick();

  // Try to scroll expanded row to the top
  const scrollable = e.component.getScrollable();
  const rowElement = e.component.getRowElement(rowIndex)?.[0];
  if (scrollable && rowElement) {
    // Calculate top offsets
    const targetOffsetTop = rowElement.getBoundingClientRect().top;
    const containerOffsetTop = scrollable.element().getBoundingClientRect().top;
    const currentScrollTop = scrollable.scrollTop();

    // Compute exact position and scroll
    const targetScrollTop = targetOffsetTop - containerOffsetTop + currentScrollTop;
    scrollable.scrollTo({ top: targetScrollTop });
  }
}

async function showChecklist(drive: IDriveStartRequest): Promise<void> {
  await checklistPopupRef?.value?.show(drive);
}

// The server pushes the transport's drives with the new unit counted.
async function addUnit(): Promise<void> {
  await unitListRef?.value?.refresh();
}
</script>

<template>
  <div class="driver-dashboard m-3">

    <DriveStartChecklistPopup ref="checklistPopupRef" @start-drive="collapseRows" />
    <UnitEdit ref="unitAddRef" wrapper-class="driver-dashboard" @update="addUnit" />

    <DxDataGrid ref="drivesGridRef" class="driver-grid" :data-source="dataSource" width="calc(100vw - 2rem)" :column-auto-width="true"
        :remote-operations="false" height="calc(100vh - 100px - 2rem)" :focused-row-enabled="false" :header-filter="{ visible: false }"
        :sorting="{ mode: 'none' }" @row-prepared="rowPrepared" @row-expanded="rowExpanded" @row-collapsed="(e: DxDataGridTypes.RowCollapsedEvent) => e.component.repaintRows([ e.component.getRowIndexByKey(e.key) ])">
      <DxKeyboardNavigation :enabled="false" />
      <DxSelection mode="single" />

      <DxToolbar>
        <DxItem location="before" template="title-template" />
        <DxItem location="after" template="refresh-button-template" />
        <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
      </DxToolbar>

      <DxColumn :caption="$t('transports.flightManifest')" cell-template="flight-cell-template" width="300px" :hiding-priority="0" />
      <DxColumn data-field="startBefore" data-type="datetime" width="200px" cell-template="startbefore-template" />
      <DxColumn :caption="$t('labels.fromTo')" cell-template="location-cell-template" />
      <DxColumn data-field="status" data-type="string" :caption="$t('labels.status')" cell-template="drive-status-template" width="150px" :alignment="'center'"
          css-class="p-0" />
      <DxColumn data-field="id" caption="" cell-template="buttons-cell-template" width="275px" :alignment="'center'"
          css-class="p-0" />
      <DxColumn type="detailExpand" :visible="false" />
      <DxColumn type="adaptive" :visible="false" />

      <DxMasterDetail :enabled="false" template="drive-detail-template" />

      <template #drive-detail-template="{ data }">
        <UnitsList :drive-id="data.key" :drive-status="data.data.status" @start-drive="showChecklist"
            @add-unit="unitAddRef?.show(data.data.transportId)" ref="unitListRef" />
      </template>

      <template #title-template>
        <span class="h3 fw-semibold">{{ $t('drivers.title') }}</span>
      </template>

      <template #refresh-button-template>
        <DxButton type="primary" @click="drivesGridRef?.instance?.refresh()">
          <ButtonContent :icon="Icons.ArrowsRotate" />
        </DxButton>
      </template>

      <template #location-cell-template="{ data }">
        <LocationCell :data="data.data" />
      </template>

      <template #startbefore-template="{ data }">
        <span>{{ toStartTime(data.data.startBefore) }}</span>
      </template>

      <template #flight-cell-template="{ data }">
        <FlightCell :data="data.data" />
      </template>

      <template #drive-status-template="{ data }">
        <DriveStatusCell :data="data.data" />
      </template>

      <template #buttons-cell-template="{ data }">
        <ButtonsCell :data="data.data" :expanded="data.row.isExpanded" @expand="expandRow" @collapse="collapseRows" />
      </template>
    </DxDataGrid>
  </div>
</template>

<style scoped>

</style>